import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Drawer,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import SnoozeIcon from '@mui/icons-material/Snooze';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useAuthReady } from '@/hooks/useAuthReady';
import { isClientUser } from '@/app/router/helpers';
import { useApiGet } from '@/hooks/useApiQuery';
import { isPrescriber } from '@/utils/prescriberHelpers';
import { parseAsUtc, dayjs } from '@/utils/dateUtils';
import DoseSpotDialog from '../../my-settings/DoseSpotDialog';
import type { ActivityItem } from '../hooks/useActivityCenter';
import { useActivityFeed, useActivityUnreadCount, useActivityCenterActions } from '../hooks/useActivityCenter';
import {
  getAdditionalDoseSpotNotificationCount,
  getDoseSpotNotificationItems,
  isDoseSpotNotificationItem,
  shouldPollDoseSpotNotifications,
  type DoseSpotNotificationCounts,
} from './activityCenterHelpers';
import {
  ACTIVITY_ACTION_RESEND_INVITE,
  parseActivityAction,
  resendPortalInviteAction,
} from './activityItemActions';

const DRAWER_WIDTH = 420;

interface DoseSpotAccessStatus {
  dosespot_seat_assigned?: boolean;
  enrolled?: boolean;
}

interface SnoozeOption {
  label: string;
  until: () => dayjs.Dayjs;
}

function useSnoozeOptions(): SnoozeOption[] {
  return [
    { label: 'Later today', until: () => dayjs(Date.now()).add(4, 'hour').startOf('hour') },
    { label: 'Tomorrow morning', until: () => dayjs(Date.now()).add(1, 'day').hour(9).minute(0).second(0) },
    {
      label: 'Next workday',
      until: () => {
        const next = dayjs(Date.now()).add(1, 'day');
        const day = next.day();
        const daysUntilMonday = day === 0 ? 1 : day === 6 ? 2 : 0;
        return next.add(daysUntilMonday, 'day').hour(9).minute(0).second(0);
      },
    },
    { label: 'Next week', until: () => dayjs(Date.now()).add(7, 'day').startOf('hour') },
  ];
}

function formatTimestamp(iso: string) {
  return parseAsUtc(iso).format('MMM D, h:mm A');
}

function getPriorityColor(priority: string | null) {
  switch (priority) {
    case 'urgent':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return 'default';
  }
}

function ActivityItemRow({
  item,
  onOpen,
  onSnooze,
  onMarkRead,
  onDismiss,
  onItemAction,
}: {
  item: ActivityItem;
  onOpen: (item: ActivityItem) => void;
  onSnooze: (item: ActivityItem, until: string) => void;
  onMarkRead: (item: ActivityItem) => void;
  onDismiss: (item: ActivityItem) => void;
  onItemAction: (item: ActivityItem, arg: string) => void;
}) {
  const snoozeOptions = useSnoozeOptions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [customDate, setCustomDate] = useState('');
  const open = Boolean(anchorEl);

  const isDoseSpotNotification = isDoseSpotNotificationItem(item.id);
  const handleSnooze = (until: dayjs.Dayjs) => {
    onSnooze(item, until.toISOString());
    setAnchorEl(null);
  };

  const isAction = item.item_type === 'action_required';
  const itemAction = item.deep_link ? parseActivityAction(item.deep_link) : null;
  const showActivityActions = !isDoseSpotNotification;

  return (
    <ListItem
      disablePadding
      secondaryAction={
        showActivityActions ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {itemAction?.verb === ACTIVITY_ACTION_RESEND_INVITE && (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  onItemAction(item, itemAction.arg);
                }}
                aria-label="resend link"
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Resend link
              </Button>
            )}
            {isAction && (
              <Tooltip title="Mark read">
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkRead(item);
                  }}
                  aria-label="mark read"
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isAction && (
              <Tooltip title="Snooze">
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                  }}
                  aria-label="snooze"
                >
                  <SnoozeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={isAction ? 'Dismiss' : 'Dismiss update'}>
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(item);
                }}
                aria-label="dismiss"
              >
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
              {snoozeOptions.map((opt) => (
                <MenuItem
                  key={opt.label}
                  onClick={() => handleSnooze(opt.until())}
                  dense
                >
                  {opt.label}
                </MenuItem>
              ))}
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  Pick date/time
                </Typography>
                <input
                  type="datetime-local"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ fontSize: '0.875rem' }}
                />
                <Button
                  size="small"
                  disabled={!customDate}
                  onClick={() => {
                    if (customDate) {
                      handleSnooze(dayjs(customDate));
                    }
                  }}
                  sx={{ mt: 0.5 }}
                >
                  Snooze
                </Button>
              </Box>
            </Menu>
          </Stack>
        ) : undefined
      }
      sx={{ pr: showActivityActions ? (isAction ? 12 : 6) : 2 }}
    >
      <ListItemButton onClick={() => onOpen(item)} selected={item.read_state === 'unread'}>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                fontWeight={item.read_state === 'unread' ? 'bold' : 'regular'}
              >
                {item.title}
              </Typography>
              {item.priority && item.priority !== 'low' && (
                <Typography
                  variant="caption"
                  color={`${getPriorityColor(item.priority)}.main`}
                >
                  {item.priority}
                </Typography>
              )}
            </Box>
          }
          secondary={
            <Box component="span" display="block">
              {item.message && (
                <Typography component="span" variant="caption" color="text.secondary" display="block">
                  {item.message}
                </Typography>
              )}
              <Typography component="span" variant="caption" color="text.secondary">
                {formatTimestamp(item.updated_at)}
              </Typography>
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );
}

function ActivityCenter() {
  const { user } = useAuthReady();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [doseSpotNotificationsOpen, setDoseSpotNotificationsOpen] = useState(false);
  const { data: feed, isLoading } = useActivityFeed(true);
  const { data: unread } = useActivityUnreadCount();
  const { markRead, dismiss, snooze } = useActivityCenterActions();
  const { data: doseSpotAccess } = useApiGet<DoseSpotAccessStatus>(
    user && !isClientUser(user) ? ['erx', 'prescriber', 'status'] : null,
    '/erx/prescriber/status',
    { retry: false },
  );
  const { data: doseSpotCounts } = useApiGet<DoseSpotNotificationCounts>(
    shouldPollDoseSpotNotifications(user, doseSpotAccess)
      ? ['erx', 'notifications', 'counts']
      : null,
    '/erx/notifications/counts',
    { refetchInterval: 15_000, retry: false },
  );

  const doseSpotItems = useMemo(
    () => (
      doseSpotAccess?.dosespot_seat_assigned === true
        ? getDoseSpotNotificationItems(doseSpotCounts || {}, feed?.items || [])
        : []
    ),
    [doseSpotAccess, doseSpotCounts, feed],
  );
  const doseSpotUnreadCount = useMemo(
    () => (
      isPrescriber(user)
        ? getAdditionalDoseSpotNotificationCount(doseSpotCounts || {}, feed?.items || [])
        : 0
    ),
    [doseSpotCounts, feed, user],
  );
  const unreadCount = unread === undefined
    ? null
    : (unread?.unread_count || 0) + doseSpotUnreadCount;

  const { attentionItems, updateItems } = useMemo(() => {
    const items = [...doseSpotItems, ...(feed?.items || [])];
    return {
      attentionItems: items.filter((i) => i.item_type === 'action_required'),
      updateItems: items.filter((i) => i.item_type === 'informational'),
    };
  }, [doseSpotItems, feed]);

  const handleOpen = useCallback(
    (item: ActivityItem) => {
      if (isDoseSpotNotificationItem(item.id)) {
        setDoseSpotNotificationsOpen(true);
        setDrawerOpen(false);
        return;
      }
      if (item.read_state === 'unread') {
        markRead(item.id).catch(() => {});
      }
      const action = item.deep_link ? parseActivityAction(item.deep_link) : null;
      // `action:` links are not navigations — the item's inline action
      // button (e.g. Resend link) triggers them instead.
      if (!action && item.deep_link) {
        navigate(item.deep_link);
      } else if (!action && item.source === 'task') {
        navigate('/tasks');
      } else if (!action && item.source === 'note') {
        navigate('/notes');
      } else if (!action && ['care_message', 'team_message'].includes(item.source)) {
        const tab = item.source === 'team_message' ? 'staff' : 'client';
        navigate(`/messages?tab=${tab}`);
      } else if (!action) {
        navigate('/');
      }
      setDrawerOpen(false);
    },
    [markRead, navigate],
  );

  const handleSnooze = useCallback(
    (item: ActivityItem, until: string) => {
      snooze({ itemId: item.id, until }).catch(() => {});
    },
    [snooze],
  );

  const handleItemAction = useCallback(
    (item: ActivityItem, arg: string) => {
      void resendPortalInviteAction(arg).then((sent) => {
        if (sent) dismiss(item.id).catch(() => {});
      });
    },
    [dismiss],
  );

  const handleDismiss = useCallback(
    (item: ActivityItem) => {
      dismiss(item.id).catch(() => {});
    },
    [dismiss],
  );

  const handleMarkRead = useCallback(
    (item: ActivityItem) => {
      markRead(item.id).catch(() => {});
    },
    [markRead],
  );

  if (!user || isClientUser(user)) return null;

  return (
    <>
      <IconButton
        color="inherit"
        onClick={() => setDrawerOpen(true)}
        aria-label="notifications"
        title="Notifications"
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: DRAWER_WIDTH } },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Notifications</Typography>
          <IconButton onClick={() => setDrawerOpen(false)} aria-label="close activity center">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {isLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Loading...
            </Typography>
          )}

          {!isLoading && attentionItems.length === 0 && updateItems.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Nothing needs your attention right now.
            </Typography>
          )}

          {attentionItems.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Needs Attention
                </Typography>
              </Box>
              <List dense disablePadding>
                {attentionItems.map((item) => (
                  <ActivityItemRow
                    key={item.id}
                    item={item}
                    onOpen={handleOpen}
                    onSnooze={handleSnooze}
                    onMarkRead={handleMarkRead}
                    onDismiss={handleDismiss}
                    onItemAction={handleItemAction}
                  />
                ))}
              </List>
            </>
          )}

          {updateItems.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Updates
                </Typography>
              </Box>
              <List dense disablePadding>
                {updateItems.map((item) => (
                  <ActivityItemRow
                    key={item.id}
                    item={item}
                    onOpen={handleOpen}
                    onSnooze={handleSnooze}
                    onMarkRead={handleMarkRead}
                    onDismiss={handleDismiss}
                    onItemAction={handleItemAction}
                  />
                ))}
              </List>
            </>
          )}
        </Box>
      </Drawer>
      <DoseSpotDialog
        open={doseSpotNotificationsOpen}
        onClose={() => setDoseSpotNotificationsOpen(false)}
        notificationSummary
      />
    </>
  );
}

export default ActivityCenter;
